import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { seoArticles } from '@/mocks/siteData';

function getArticleSections(slug: string): { title: string; content: string[] }[] {
  const sections: Record<string, { title: string; content: string[] }[]> = {
    'what-is-itaobuy-spreadsheet': [
      {
        title: 'Introduction to Itaobuy Spreadsheet',
        content: [
          'In the rapidly evolving world of cross-border e-commerce, finding an efficient way to source products from China has become essential for businesses and individual buyers alike. The <strong>itaobuy spreadsheet</strong> system has emerged as a game-changing solution that simplifies the entire sourcing process through a structured, transparent approach.',
          'Unlike traditional methods where buyers spend countless hours manually browsing Taobao, 1688, and other Chinese marketplaces, the <strong>itaobuy spreadsheet</strong> consolidates curated product listings into an organized, easy-to-navigate format. This innovative system bridges the gap between international buyers and Chinese suppliers, making cross-border shopping accessible to everyone.',
        ],
      },
      {
        title: 'How the Itaobuy Spreadsheet System Works',
        content: [
          'The <strong>itaobuy spreadsheet</strong> operates on a straightforward yet powerful principle: organize, compare, and act. Instead of dealing with the cluttered interfaces of traditional Chinese e-commerce platforms, users interact with a clean spreadsheet that presents all necessary information at a glance.',
          'The five-step workflow includes browsing curated spreadsheet products, selecting items from structured lists, submitting orders through the agent system, receiving quality check images, and shipping globally to your address. Each step is designed to maximize transparency and minimize confusion.',
        ],
      },
      {
        title: 'Key Features and Benefits',
        content: [
          'The <strong>itaobuy spreadsheet</strong> system offers transparent pricing structure, easy product comparison, reduced sourcing risk, beginner-friendly interface, faster decision making, and global accessibility. Every cost is broken down clearly — product price, domestic shipping, international freight, and service fees are all visible upfront.',
        ],
      },
      {
        title: 'Itaobuy Spreadsheet vs Traditional Shopping Methods',
        content: [
          'Traditional methods require navigating complex websites in Chinese, dealing with fragmented product information, and manually calculating total costs. The <strong>itaobuy spreadsheet</strong> eliminates these pain points by centralizing everything into one clear, structured format.',
        ],
      },
      {
        title: 'Who Should Use Itaobuy Spreadsheet?',
        content: [
          'The system is designed for small business owners sourcing inventory cost-effectively, dropshippers finding reliable products, fashion retailers accessing trending styles, individual shoppers buying quality products at factory-direct prices, and e-commerce entrepreneurs building scalable online stores.',
        ],
      },
    ],
    'itaobuy-vs-taobao-direct': [
      {
        title: 'Understanding the Two Approaches',
        content: [
          'When sourcing products from China, international buyers typically face a choice between shopping directly on Taobao or using a structured system like the <strong>itaobuy spreadsheet</strong>. While both methods connect you to Chinese products, they differ dramatically in experience, efficiency, and transparency.',
          'Shopping directly on Taobao means navigating a Chinese-language platform with thousands of individual sellers, inconsistent product information, and complex shipping logistics. The <strong>itaobuy spreadsheet</strong> transforms this experience by organizing curated products into an easy-to-browse format.',
        ],
      },
      {
        title: 'Price Transparency Comparison',
        content: [
          'On Taobao, final costs are often unclear until checkout — domestic shipping, agent fees, and international freight are frequently hidden or estimated. The <strong>itaobuy spreadsheet</strong> provides complete cost breakdowns upfront, so you know exactly what you are paying before committing to any purchase.',
        ],
      },
      {
        title: 'User Experience and Accessibility',
        content: [
          'Taobao requires Chinese language skills or constant use of translation tools, plus familiarity with Chinese e-commerce conventions. The <strong>itaobuy spreadsheet</strong> is fully in English with an intuitive interface designed for international users. No prior experience with Chinese marketplaces is required.',
        ],
      },
      {
        title: 'Quality Control and Support',
        content: [
          'When shopping directly, quality verification is entirely your responsibility, with limited recourse if products do not match descriptions. The <strong>itaobuy spreadsheet</strong> system includes systematic QC image verification and agent support throughout the process.',
        ],
      },
    ],
    'itaobuy-spreadsheet-guide': [
      {
        title: 'Why the Itaobuy Spreadsheet Method Matters',
        content: [
          'Cross-border product sourcing from China has traditionally been confusing for international buyers. Language barriers, unclear pricing, and fragmented supplier information create friction at every step. The <strong>itaobuy spreadsheet</strong> system addresses these challenges head-on by reimagining sourcing as an organized, transparent process.',
        ],
      },
      {
        title: 'Getting Started with Itaobuy Spreadsheet',
        content: [
          'Beginning your journey with the <strong>itaobuy spreadsheet</strong> is remarkably straightforward. You start by browsing organized product categories, each containing curated items with detailed specifications. The spreadsheet format makes it easy to compare products side by side.',
        ],
      },
      {
        title: 'Maximizing Your Sourcing Efficiency',
        content: [
          'To get the most out of the <strong>itaobuy spreadsheet</strong> system, focus on understanding the pricing breakdown, reviewing QC images carefully, and building relationships with reliable suppliers through consistent ordering. The system rewards organized buyers who take advantage of its structured format.',
        ],
      },
      {
        title: 'Real Results from Real Users',
        content: [
          'Thousands of buyers worldwide have transformed their sourcing workflows using the <strong>itaobuy spreadsheet</strong> system. From boutique owners in London to dropshippers in Sydney, users consistently report significant time savings, better price awareness, and fewer sourcing-related headaches.',
        ],
      },
    ],
  };

  const defaultSections = [
    {
      title: 'Understanding the Topic',
      content: [
        `The <strong>itaobuy spreadsheet</strong> system provides comprehensive coverage of this important sourcing topic. Understanding these concepts is essential for getting the most value from your cross-border shopping experience.`,
        `This article explores key aspects of the <strong>itaobuy spreadsheet</strong> system that every international buyer should know. Whether you are a seasoned importer or just starting your sourcing journey, the information here will help you make more informed decisions.`,
      ],
    },
    {
      title: 'Key Insights',
      content: [
        `The <strong>itaobuy spreadsheet</strong> approach offers unique advantages that traditional methods cannot match. By organizing product data in a structured format, buyers gain clarity and confidence at every step of the purchasing process.`,
        `Transparency is at the core of the <strong>itaobuy spreadsheet</strong> philosophy. Every cost, every spec, and every timeline is clearly displayed, eliminating the guesswork that plagues conventional cross-border shopping.`,
      ],
    },
    {
      title: 'Practical Recommendations',
      content: [
        `To apply these insights effectively with the <strong>itaobuy spreadsheet</strong> system, start by familiarizing yourself with the category structure and pricing format. Take time to compare products across different categories before committing to purchases.`,
        `The <strong>itaobuy spreadsheet</strong> system works best when you approach it with a clear sourcing strategy. Define your product needs, set a budget, and use the spreadsheet comparisons to find the best value for your requirements.`,
      ],
    },
  ];

  return sections[slug] || defaultSections;
}

const faqs: Record<string, { q: string; a: string }[]> = {
  'what-is-itaobuy-spreadsheet': [
    { q: 'Is the itaobuy spreadsheet system free to use?', a: 'The itaobuy spreadsheet interface is accessible for browsing and product comparison. Actual purchasing involves standard service and shipping fees, all of which are transparently displayed in the spreadsheet before you commit to any order.' },
    { q: 'How long does shipping take with itaobuy spreadsheet?', a: 'Shipping times vary depending on your location and the shipping method you select. Standard international shipping typically ranges from 7-21 business days, while express options can deliver in 3-7 business days.' },
    { q: 'Can I return products purchased through the itaobuy spreadsheet?', a: 'Return policies depend on the specific products and suppliers. The itaobuy spreadsheet system helps minimize the need for returns by providing QC images before shipping, so you can verify product quality in advance.' },
    { q: 'What payment methods are accepted?', a: 'Multiple payment methods are supported through the itaobuy spreadsheet platform, including major credit cards, PayPal, and bank transfers. All payment details are clearly listed during the checkout process.' },
  ],
};

const defaultFaqs = [
  { q: 'Is the itaobuy spreadsheet system beginner-friendly?', a: 'Yes, the itaobuy spreadsheet system is designed specifically with beginners in mind. The interface is intuitive, all pricing is transparent, and no prior experience with Chinese marketplaces is required to start sourcing products effectively.' },
  { q: 'How does pricing work with itaobuy spreadsheet?', a: 'Pricing in the itaobuy spreadsheet is fully transparent. You see the product cost, domestic shipping within China, international shipping to your destination, and any service fees — all broken down clearly before you place an order.' },
  { q: 'What kind of products can I source?', a: 'The itaobuy spreadsheet system covers a wide range of product categories including footwear, apparel, accessories, bags, belts, perfumes, and much more. Browse through organized categories to discover what is available.' },
];

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = seoArticles.find((a) => a.slug === slug);
  const sections = getArticleSections(slug || '');
  const articleFaqs = faqs[slug || ''] || defaultFaqs;

  useEffect(() => {
    if (!article) return;
    const articleSchema = document.createElement('script');
    articleSchema.type = 'application/ld+json';
    articleSchema.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      datePublished: '2026-06-28',
      dateModified: '2026-06-28',
      author: { '@type': 'Organization', name: 'itaobuy.living' },
      publisher: { '@type': 'Organization', name: 'itaobuy.living' },
    });
    document.head.appendChild(articleSchema);

    const faqSchema = document.createElement('script');
    faqSchema.type = 'application/ld+json';
    faqSchema.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: articleFaqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    document.head.appendChild(faqSchema);

    return () => {
      document.head.removeChild(articleSchema);
      document.head.removeChild(faqSchema);
    };
  }, [article, articleFaqs]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist. Browse our available guides below.</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap">
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        <article>
          <header className="pt-24 pb-12 md:pb-16 bg-background-100">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="breadcrumb">
                <Link to="/" className="hover:text-foreground-950 transition-colors whitespace-nowrap">Home</Link>
                <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center"></i>
                <Link to="/shopping-guide" className="hover:text-foreground-950 transition-colors whitespace-nowrap">Shopping Guide</Link>
                <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center"></i>
                <span className="text-foreground-950 font-medium truncate">{article.title}</span>
              </nav>

              <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
                <div className="lg:w-3/5">
                  <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
                    {article.title}
                  </h1>
                  <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500">
                    <span>Updated: June 2026</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                    <span>8 min read</span>
                  </div>
                </div>

                <div className="lg:w-2/5">
                  <div className="rounded-2xl overflow-hidden aspect-[16/10] bg-background-200">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </header>

          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
              <div className="lg:w-3/5">
                <div className="prose-custom">
                  {sections.map((section, sIdx) => (
                    <section key={sIdx} className="mb-12" id={section.title.toLowerCase().replace(/\s+/g, '-')}>
                      <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                        {section.title}
                      </h2>
                      {section.content.map((paragraph, pIdx) => (
                        <p key={pIdx} className="text-foreground-700 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: paragraph }}></p>
                      ))}
                    </section>
                  ))}

                  <section className="mb-12">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                      Frequently Asked Questions
                    </h2>
                    <div className="space-y-4">
                      {articleFaqs.map((faq, index) => (
                        <details key={index} className="group rounded-xl border border-background-200/70 bg-background-50 overflow-hidden">
                          <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-foreground-950 text-sm md:text-base">
                            {faq.q}
                            <i className="ri-add-line w-5 h-5 flex items-center justify-center text-foreground-500 group-open:hidden"></i>
                            <i className="ri-subtract-line w-5 h-5 hidden items-center justify-center text-foreground-500 group-open:flex"></i>
                          </summary>
                          <div className="px-5 pb-4 text-foreground-600 text-sm leading-relaxed">
                            {faq.a}
                          </div>
                        </details>
                      ))}
                    </div>
                  </section>

                  <section className="p-6 md:p-8 rounded-2xl bg-primary-50 border border-primary-200 text-center">
                    <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-3">
                      Ready to Start Sourcing Smarter?
                    </h2>
                    <p className="text-foreground-700 leading-relaxed mb-5 max-w-lg mx-auto">
                      Join thousands of buyers who have transformed their sourcing workflow with the <strong>itaobuy spreadsheet</strong> system. Start browsing curated products today.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <a
                        href="https://kaspreadsheet.com"
                        target="_blank"
                        rel="noopener nofollow"
                        className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                      >
                        ITAOBUY SHEET
                        <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
                      </a>
                      <Link
                        to="/"
                        className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-foreground-300 text-foreground-700 text-sm font-medium hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 transition-all duration-200 cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                        Back to Home
                      </Link>
                    </div>
                  </section>
                </div>
              </div>

              <aside className="lg:w-2/5">
                <div className="sticky top-24 space-y-8">
                  <div className="rounded-2xl bg-background-100 border border-background-200/70 p-6">
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">Quick Navigation</h3>
                    <ul className="space-y-2">
                      {sections.map((s) => (
                        <li key={s.title}>
                          <a href={`#${s.title.toLowerCase().replace(/\s+/g, '-')}`} className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors block py-1.5 whitespace-nowrap">
                            {s.title}
                          </a>
                        </li>
                      ))}
                      <li>
                        <a href="#frequently-asked-questions" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors block py-1.5 whitespace-nowrap">
                          Frequently Asked Questions
                        </a>
                      </li>
                    </ul>
                  </div>

                  <div className="rounded-2xl bg-primary-50 border border-primary-200 p-6 text-center">
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-3">
                      Explore Itaobuy Spreadsheet
                    </h3>
                    <p className="text-sm text-foreground-600 mb-4">
                      Browse curated product categories sourced through the itaobuy spreadsheet system.
                    </p>
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                    >
                      BUY IT NOW
                      <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
                    </a>
                  </div>

                  <div className="rounded-2xl bg-background-50 border border-background-200/70 p-6">
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">Related Categories</h3>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { name: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
                        { name: 'Hoodies', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
                        { name: 'T-Shirts', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
                        { name: 'Jackets', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html' },
                        { name: 'Bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
                        { name: 'Accessories', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html' },
                      ].map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200/70 text-xs text-foreground-700 hover:border-foreground-300 hover:text-foreground-950 transition-all cursor-pointer whitespace-nowrap"
                        >
                          {cat.name}
                        </a>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-2xl bg-background-100 border border-background-200/70 p-6 text-center">
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                      Back to Home
                    </Link>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}